<template>
    <div class="map-where">
        <div class="map-controls">
            <select
                class="form-select form-select-sm"
                v-model="selected"
                @change="openPopupFixed"
            >
                <option :value="0">{{ trans('countries.all') }}</option>
                <option v-for="country in countries" :key="country.id" :value="country.id">
                    {{ country.names[lang] }}
                </option>
            </select>
        </div>
        <div ref="map" class="map-container"></div>
        <div class="map-legend p-1 d-flex justify-content-between">
            <div v-for="(level, index) in levels" :key="index">
                <span class="d-inline-block rounded-1" style="width: 1rem" :style="{background: level.color}">&nbsp;</span>
                <span class="d-inline-block ms-1"><span v-if="level.gte > 0">≥</span> {{level.gte}} {{ trans('projects.label') }}</span>
            </div>
        </div>
    </div>
</template>

<style lang="postcss" scoped>
    .map-controls {
        z-index: 800;
        padding: 0.25rem;
        background-color: #aaa;
        .form-select {
            width: auto;
        }
    }
    .map-container {
        min-height: 600px;
        background-color: #CCE3F3;
    }
    .map-legend {

    }
</style>

<style lang="postcss">
    .map-where-popup {
        min-height: 350px;
    }
</style>


<script>
import 'apexcharts/dist/apexcharts.css'

import L from './Leaflet'

import axios from 'axios'
import { createApp, h as createVNode, markRaw } from 'vue'

import { dataUrl, currentLang, trans } from './AppUtils'
import MapPopup from './MapPopup.vue'


const HTTP = axios.create()

/**
 * The GeoJSON structure for the countries
 */
let geoWorld = null
let chartSequence = 1
let countryList = []


export default {

    emits: [
        'filter',
        'focus',
        'scroll',
    ],

    components: [
        // MapPopup
    ],

    props: {
        units: {
            type: Array,
            default: () => []
        },
        taxonomy: {
            type: Object,
            default: () => {},
        }
    },

    data() {
        return {
            leafletMap: null,
            activeLayer: null,
            countries: countryList,
            lang: currentLang(),
            selected: 0,
            levels: [
                {gte: 10, color: '#253494'},    //
                {gte: 5 , color: '#2c7fb8'},
                {gte: 3 , color: '#41b6c4'},
                {gte: 1 , color: '#a1dab4'},
                {gte: 0 , color: '#999999'},
            ],
        }
    },

    computed: {
        unitTable() {
            let table = {}
            for (const u of this.units) {
                table[u.properties.code] = u
            }
            return table
        }
    },

    watch: {
        selected(id) {
            // this.$emit('filter', {c: (id) ? [id] : []})
            // const focus = this.countries.find(c => c.id == id)
            // console.debug("FOCUS", focus)
        },
        units() {
            // console.debug("UNITS", u)
            this.updateLayer()
        }
    },

    mounted() {

        // [CRS]
        var crs = L.extend({}, L.CRS.EPSG4326, {
            scale: function (z) {
                return 384 * Math.pow(2, z)
            },
            transformation: new L.Transformation(1 / 360, 0.5, -1 / 360, 0.25)
        })

        const map = L.map(this.$refs.map, {
            crs: crs,
            zoom: 1.5,
            center: [15, 20],
            minZoom: 1,
            maxZoom: 5,
            zoomSnap: 0.25,
            zoomControl: false,
            attributionControl: false,
            gestureHandling: true,
            // @doc https://github.com/elmarquis/Leaflet.GestureHandling
            // gestureHandlingOptions: {}
        })
        L.control.attribution({position: 'bottomright'}).addTo(map)
        L.control.zoom({position: 'topright'}).addTo(map)
        this.addResetButtonTo(map)
        this.leafletMap = markRaw(map)

        // this.updateLayer()
        // console.debug("MOUNTED")
    },

    methods: {

        trans,

        addResetButtonTo(map) {
            const reset = new L.Control({position:'topright'})
            reset.onAdd = function(map) {
                var azoom = L.DomUtil.create('div','leaflet-bar map-home')
                azoom.innerHTML = `
                    <a href="#" title="Reset view">
                        <svg class="svg-inline--fa fa-globe-africa" style="width:1rem" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="globe-africa" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512"><path fill="currentColor" d="M248 8C111.03 8 0 119.03 0 256s111.03 248 248 248 248-111.03 248-248S384.97 8 248 8zm160 215.5v6.93c0 5.87-3.32 11.24-8.57 13.86l-15.39 7.7a15.485 15.485 0 0 1-15.53-.97l-18.21-12.14a15.52 15.52 0 0 0-13.5-1.81l-2.65.88c-9.7 3.23-13.66 14.79-7.99 23.3l13.24 19.86c2.87 4.31 7.71 6.9 12.89 6.9h8.21c8.56 0 15.5 6.94 15.5 15.5v11.34c0 3.35-1.09 6.62-3.1 9.3l-18.74 24.98c-1.42 1.9-2.39 4.1-2.83 6.43l-4.3 22.83c-.62 3.29-2.29 6.29-4.76 8.56a159.608 159.608 0 0 0-25 29.16l-13.03 19.55a27.756 27.756 0 0 1-23.09 12.36c-10.51 0-20.12-5.94-24.82-15.34a78.902 78.902 0 0 1-8.33-35.29V367.5c0-8.56-6.94-15.5-15.5-15.5h-25.88c-14.49 0-28.38-5.76-38.63-16a54.659 54.659 0 0 1-16-38.63v-14.06c0-17.19 8.1-33.38 21.85-43.7l27.58-20.69a54.663 54.663 0 0 1 32.78-10.93h.89c8.48 0 16.85 1.97 24.43 5.77l14.72 7.36c3.68 1.84 7.93 2.14 11.83.84l47.31-15.77c6.33-2.11 10.6-8.03 10.6-14.7 0-8.56-6.94-15.5-15.5-15.5h-10.09c-4.11 0-8.05-1.63-10.96-4.54l-6.92-6.92a15.493 15.493 0 0 0-10.96-4.54H199.5c-8.56 0-15.5-6.94-15.5-15.5v-4.4c0-7.11 4.84-13.31 11.74-15.04l14.45-3.61c3.74-.94 7-3.23 9.14-6.44l8.08-12.11c2.87-4.31 7.71-6.9 12.89-6.9h24.21c8.56 0 15.5-6.94 15.5-15.5v-21.7C359.23 71.63 422.86 131.02 441.93 208H423.5c-8.56 0-15.5 6.94-15.5 15.5z"></path></svg>
                    </a>
                    ` //
                L.DomEvent
                    .disableClickPropagation(azoom)
                    .addListener(azoom, 'click', function(ev) {
                        map.setView(map.options.center, map.options.zoom)
                        L.DomEvent.stopPropagation(ev)
                        return false
                    }, azoom)
                return azoom
            }
            reset.addTo(map)
            return reset
        },

        /**
         *
         */
        async updateLayer() {

            const stroke = '#fff'
            const units = this.unitTable
            const self = this
            const lang = currentLang()
            const levels = this.levels

            if (geoWorld === null) {
                try {
                    const re = await HTTP.get(dataUrl("admin-units").href)
                    geoWorld = re.data
                    countryList = re.data.features
                        .map(f => f.properties)
                        .sort((a,b) => {
                            const x = a.names[lang]
                            const y = b.names[lang]
                            return (x < y) ? -1 : (x > y ? +1 : 0)
                        })
                    // this.$emit('countries', countryList)
                    this.countries = countryList
                } catch (err) {
                    console.error(err)
                }
            }

            const levelColor = function (size) {
                let color = '#999'
                for (const spec of levels) {
                    if (size >= spec.gte) {
                        color = spec.color
                        break
                    }
                }
                return color
            }

            const layer = L.geoJSON(geoWorld, {
                pane: 'tilePane',
                style: function (feature) {
                    const u = units[feature.properties.code]
                    return {
                        color: stroke,
                        fillColor: levelColor(u ? u.properties.projects.count : 0),
                        weight: 1,
                        fillOpacity: 1
                    };
                },
                onEachFeature: function (feature, layer) {
                    layer.on({
                        mouseover: function (e) {
                            e.target.setStyle({fillOpacity: 0.8});
                        },
                        mouseout: function (e) {
                            e.target.setStyle({fillOpacity: 1.0});
                        },
                        click: function (e) {
                            self.openPopup(
                                e.latlng,
                                feature.properties,
                                units[feature.properties.code]
                            )
                        }
                    });
                }
            })

            if (this.activeLayer) {
                this.leafletMap.removeLayer(this.activeLayer)
            }

            this.activeLayer = layer
            this.leafletMap.addLayer(this.activeLayer)
        },

        /**
         *
         * @param {*} coords
         * @param {*} iso
         */
        async openPopup(coords, properties, unit) {

            const map = this.leafletMap
            const sequence = chartSequence
            const country = {
                id: properties.id,
                code: properties.code,
                names: properties.names,
                projects: (unit)
                    ? unit.properties.projects
                    : {count: 0, dist: {}}
            }

            const popApp = createApp(
                createVNode(MapPopup, {
                    onFilter: filter => {
                        this.$emit('filter', filter)
                        this.$emit('scroll', 'result')
                    }
                }),
                {
                    taxonomy: this.taxonomy,
                    country: country,
                }
            )

            const popup = L.popup({
                minWidth: 400,
                keepInView: true,
                className: "map-where-popup"
            })

            popup.setLatLng(coords)
            popup.setContent(`<div id="country-popup-${sequence}"></div>`)
            popup.on('remove', () => {
                this.selected = 0
                this.$emit('filter', {c: []})
            })

            map.openPopup(popup)

            popApp.mount(`#country-popup-${sequence}`)
            this.$emit('focus', country)

            this.selected = country.id
            chartSequence++
        },

        openPopupFixed(e) {
            const units = this.unitTable
            const id = e.target.value
            const focus = this.countries.find(c => c.id == id)
            if (focus) {
                // console.debug("FOCUS", focus, units[focus.code])
                this.openPopup(
                    focus.marker,
                    focus,
                    units[focus.code]
                )
            } else {
                this.leafletMap.closePopup()
            }
        },

        closePopup() {
            this.leafletMap.closePopup()
        }

    }
}
</script>